/**
 * HedgeOptionsMonitor.jsx
 *
 * WTI crude oil options chain monitor and premium cost analysis tool
 * for the Aviator Energy daily dashboard hedge module.
 *
 * Usage:
 *   <Route path="/hedge/options" element={<HedgeOptionsMonitor />} />
 *
 * API:
 *   GET /api/hedge/options  — options chain data (options[], months[])
 *   GET /api/hedge          — portfolio overview (forecasts[] with Net_Oil_BBL)
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer
} from 'recharts'
import { useTheme } from '../context/ThemeContext'
import { apiConfig } from '../auth/authConfig'

// ─── Formatting helpers ───────────────────────────────────────────────────────

function formatMonth(isoDate) {
    if (!isoDate) return ''
    const clean = typeof isoDate === 'string' ? isoDate.slice(0, 10) : new Date(isoDate).toISOString().slice(0, 10)
    const d = new Date(clean + 'T00:00:00')
    if (isNaN(d)) return ''
    return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
}

function formatDollars(val) {
    if (val == null || isNaN(val)) return '$0'
    const abs = Math.abs(val)
    const sign = val < 0 ? '-' : ''
    if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`
    if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`
    return `${sign}$${abs.toFixed(2)}`
}

function formatNumber(val) {
    if (val == null || isNaN(val)) return '0'
    return Math.round(val).toLocaleString('en-US')
}

function formatPct(val) {
    if (val == null || isNaN(val)) return '—'
    return `${(val * 100).toFixed(1)}%`
}

function formatPrice(val) {
    if (val == null || isNaN(val)) return '—'
    return `$${val.toFixed(2)}`
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function LoadingSpinner({ isDark }) {
    return (
        <div className="flex items-center justify-center h-64" role="status" aria-label="Loading options data">
            <div className={`w-10 h-10 border-4 rounded-full animate-spin ${
                isDark
                    ? 'border-slate-600 border-t-blue-400'
                    : 'border-gray-200 border-t-blue-500'
            }`} />
        </div>
    )
}

function ErrorBanner({ message, onRetry, isDark }) {
    return (
        <div className={`rounded-lg p-4 border ${
            isDark
                ? 'bg-red-900/30 border-red-700 text-red-300'
                : 'bg-red-50 border-red-300 text-red-700'
        }`} role="alert">
            <p className="font-medium">Failed to load options data</p>
            <p className="text-sm mt-1 opacity-80">{message}</p>
            {onRetry && (
                <button
                    onClick={onRetry}
                    className={`mt-3 text-sm px-3 py-1 rounded transition ${
                        isDark
                            ? 'bg-red-800 hover:bg-red-700 text-white'
                            : 'bg-red-600 hover:bg-red-700 text-white'
                    }`}
                >
                    Retry
                </button>
            )}
        </div>
    )
}

function EmptyState({ isDark }) {
    const text = isDark ? 'text-gray-400' : 'text-gray-500'
    const subtext = isDark ? 'text-gray-500' : 'text-gray-400'
    const border = isDark ? 'border-slate-700' : 'border-gray-200'
    return (
        <div className={`rounded-xl border ${border} p-12 text-center`}>
            <div className="flex justify-center mb-4">
                <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                    isDark ? 'bg-slate-800' : 'bg-gray-100'
                }`}>
                    <svg className={`w-7 h-7 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                </div>
            </div>
            <p className={`font-semibold text-base ${text}`}>Options chain data is not yet available.</p>
            <p className={`mt-2 text-sm max-w-md mx-auto ${subtext}`}>
                Options data will be populated once CME scraping is configured.
            </p>
        </div>
    )
}

/** Custom tooltip for the IV chart */
function IVTooltip({ active, payload, label, isDark }) {
    if (!active || !payload?.length) return null
    const bg = isDark ? '#1e293b' : '#ffffff'
    const border = isDark ? '#334155' : '#e2e8f0'
    const headColor = isDark ? '#fff' : '#111827'
    return (
        <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 8, padding: '10px 14px', fontSize: 12 }}>
            <p style={{ fontWeight: 600, marginBottom: 4, color: headColor }}>Strike {formatPrice(label)}</p>
            {payload.map((entry, i) => (
                <p key={i} style={{ color: entry.color, margin: '2px 0' }}>
                    {entry.name}: {formatPct(entry.value / 100)}
                </p>
            ))}
        </div>
    )
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function HedgeOptionsMonitor() {
    const navigate = useNavigate()
    const { theme } = useTheme()
    const isDark = theme === 'dark'

    // ── Theme shorthand classes ─────────────────────────────────────────────
    const bg        = isDark ? 'bg-slate-900' : 'bg-gray-50'
    const cardBg    = isDark ? 'bg-slate-800' : 'bg-white'
    const border    = isDark ? 'border-slate-700' : 'border-gray-200'
    const text      = isDark ? 'text-white' : 'text-gray-900'
    const textMuted = isDark ? 'text-gray-400' : 'text-gray-500'
    const textSm    = isDark ? 'text-gray-300' : 'text-gray-700'
    const selectCls = `text-sm rounded-lg px-3 py-1.5 border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
        isDark
            ? 'bg-slate-700 text-white border-slate-600'
            : 'bg-white text-gray-900 border-gray-300'
    }`
    const axisColor = isDark ? '#94a3b8' : '#6b7280'
    const gridColor = isDark ? '#334155' : '#e5e7eb'

    // ── State ───────────────────────────────────────────────────────────────
    const [optionsData, setOptionsData]   = useState(null)   // raw API response
    const [hedgeData, setHedgeData]       = useState(null)   // /api/hedge response
    const [loading, setLoading]           = useState(true)
    const [error, setError]               = useState(null)
    const [selectedMonth, setSelectedMonth] = useState(null) // contract month ISO string

    // Calculator state — up to 3 comparison rows
    const [calcPutStrikes, setCalcPutStrikes]   = useState(['', '', ''])
    const [calcCallStrikes, setCalcCallStrikes] = useState(['', '', ''])

    // ── Data fetch ──────────────────────────────────────────────────────────
    const fetchData = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const [optRes, hedgeRes] = await Promise.all([
                fetch(`${apiConfig.baseUrl}/hedge/options`),
                fetch(`${apiConfig.baseUrl}/hedge`),
            ])
            if (!optRes.ok) throw new Error(`Options API error ${optRes.status}: ${optRes.statusText}`)
            const optJson   = await optRes.json()
            const hedgeJson = hedgeRes.ok ? await hedgeRes.json() : {}
            setOptionsData(optJson)
            setHedgeData(hedgeJson)
            // Default to first available month
            if (optJson.months?.length > 0) {
                setSelectedMonth(optJson.months[0])
            }
        } catch (err) {
            setError(err.message)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => { fetchData() }, [fetchData])

    // ── Derived data ────────────────────────────────────────────────────────

    /** All options for the selected contract month */
    const chainForMonth = useMemo(() => {
        if (!optionsData?.options || !selectedMonth) return []
        return optionsData.options.filter(o => o.ContractMonth === selectedMonth)
    }, [optionsData, selectedMonth])

    /** Unique sorted strikes for the selected month */
    const strikes = useMemo(() => {
        const s = new Set(chainForMonth.map(o => o.Strike))
        return Array.from(s).sort((a, b) => a - b)
    }, [chainForMonth])

    /** Build lookup: strike → { CALL, PUT } option objects */
    const strikeMap = useMemo(() => {
        const map = {}
        chainForMonth.forEach(o => {
            if (!map[o.Strike]) map[o.Strike] = {}
            map[o.Strike][o.OptionType] = o
        })
        return map
    }, [chainForMonth])

    /**
     * Current strip price — pulled from the hedge overview summary.
     * Used to determine at-the-money strike.
     */
    const stripPrice = useMemo(() => {
        return hedgeData?.summary?.avgStrip12Mo
            ?? hedgeData?.strip?.[0]?.settle
            ?? null
    }, [hedgeData])

    /** At-the-money strike index (closest to strip price) */
    const atmStrike = useMemo(() => {
        if (stripPrice == null || strikes.length === 0) return null
        return strikes.reduce((best, s) =>
            Math.abs(s - stripPrice) < Math.abs(best - stripPrice) ? s : best
        , strikes[0])
    }, [strikes, stripPrice])

    /**
     * Forecast volume for the selected month (Net_Oil_BBL from forecasts array).
     */
    const forecastBBL = useMemo(() => {
        const forecasts = hedgeData?.forecasts || []
        if (!selectedMonth || forecasts.length === 0) return null
        const match = forecasts.find(f => {
            if (!f.ForecastMonth) return false
            return f.ForecastMonth.slice(0, 10) === selectedMonth.slice(0, 10)
        })
        return match?.Net_Oil_BBL ?? null
    }, [hedgeData, selectedMonth])

    /** Available put strikes for the calculator dropdown */
    const putStrikeOptions = useMemo(() => {
        return strikes.filter(s => strikeMap[s]?.PUT)
    }, [strikes, strikeMap])

    /** Available call strikes for the calculator dropdown */
    const callStrikeOptions = useMemo(() => {
        return strikes.filter(s => strikeMap[s]?.CALL)
    }, [strikes, strikeMap])

    /** Implied volatility chart data — Puts (red) and Calls (blue) */
    const ivChartData = useMemo(() => {
        return strikes
            .map(s => ({
                strike: s,
                putIV:  strikeMap[s]?.PUT?.ImpliedVol != null  ? strikeMap[s].PUT.ImpliedVol  * 100 : null,
                callIV: strikeMap[s]?.CALL?.ImpliedVol != null ? strikeMap[s].CALL.ImpliedVol * 100 : null,
            }))
            .filter(d => d.putIV != null || d.callIV != null)
    }, [strikes, strikeMap])

    const hasIVData = ivChartData.some(d => d.putIV != null || d.callIV != null)

    // ── Calculator helpers ───────────────────────────────────────────────────

    function calcRowPremium(putStrike, callStrike) {
        const put  = putStrike  ? strikeMap[putStrike]?.PUT   : null
        const call = callStrike ? strikeMap[callStrike]?.CALL : null
        const putPremium  = put?.LastPrice  ?? null
        const callPremium = call?.LastPrice ?? null
        const netCollarPremium =
            putPremium != null && callPremium != null
                ? putPremium - callPremium
                : null
        const bbl = forecastBBL ?? 0
        return {
            putPremiumPerBBL:   putPremium,
            callPremiumPerBBL:  callPremium,
            netCollarPerBBL:    netCollarPremium,
            totalPutCost:       putPremium  != null ? putPremium  * bbl : null,
            totalCallCredit:    callPremium != null ? callPremium * bbl : null,
            totalCollarNet:     netCollarPremium != null ? netCollarPremium * bbl : null,
            volumeBBL:          bbl,
        }
    }

    function handlePutStrikeChange(idx, val) {
        setCalcPutStrikes(prev => {
            const next = [...prev]
            next[idx] = val
            return next
        })
    }

    function handleCallStrikeChange(idx, val) {
        setCalcCallStrikes(prev => {
            const next = [...prev]
            next[idx] = val
            return next
        })
    }

    // ── Change color helper ──────────────────────────────────────────────────
    function changeColor(val) {
        if (val == null) return textMuted
        if (val > 0) return isDark ? 'text-green-400' : 'text-green-600'
        if (val < 0) return isDark ? 'text-red-400'   : 'text-red-600'
        return textMuted
    }

    // ── Render ───────────────────────────────────────────────────────────────

    const hasOptions = optionsData?.options?.length > 0

    return (
        <div className={`min-h-screen ${bg} ${text} p-4 sm:p-6`}>

            {/* ── Header ─────────────────────────────────────────────────────── */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
                <button
                    onClick={() => navigate('/hedge')}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                        isDark
                            ? 'border-slate-600 text-slate-300 hover:bg-slate-700'
                            : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                    }`}
                    aria-label="Back to Hedge Overview"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Hedge
                </button>

                <div>
                    <h1 className={`text-xl font-bold ${text}`}>Options Monitor</h1>
                    <p className={`text-xs ${textMuted}`}>
                        WTI Crude Options Chain &bull; CME Group
                        {optionsData?.options?.[0]?.FetchDate && (
                            <span className="ml-2">Data as of {optionsData.options[0].FetchDate}</span>
                        )}
                        {stripPrice != null && (
                            <span className="ml-2">
                                Strip: <span className={`font-semibold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                                    {formatPrice(stripPrice)}
                                </span>
                            </span>
                        )}
                    </p>
                </div>

                <div className="ml-auto flex items-center gap-2">
                    <button
                        onClick={fetchData}
                        className={`p-1.5 rounded-lg border transition ${
                            isDark
                                ? 'border-slate-600 text-slate-400 hover:bg-slate-700'
                                : 'border-gray-300 text-gray-500 hover:bg-gray-100'
                        }`}
                        title="Refresh data"
                        aria-label="Refresh options data"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* ── Loading / Error ─────────────────────────────────────────────── */}
            {loading && <LoadingSpinner isDark={isDark} />}
            {!loading && error && <ErrorBanner message={error} onRetry={fetchData} isDark={isDark} />}

            {/* ── Empty state ─────────────────────────────────────────────────── */}
            {!loading && !error && !hasOptions && <EmptyState isDark={isDark} />}

            {/* ── Main content ────────────────────────────────────────────────── */}
            {!loading && !error && hasOptions && (
                <div className="space-y-6">

                    {/* ── Month Selector ──────────────────────────────────────── */}
                    <div className={`${cardBg} rounded-xl border ${border} px-4 py-3 flex flex-wrap items-center gap-3`}>
                        <label htmlFor="options-month-select" className={`text-sm font-medium ${textSm}`}>
                            Contract Month
                        </label>
                        <select
                            id="options-month-select"
                            value={selectedMonth || ''}
                            onChange={e => setSelectedMonth(e.target.value)}
                            className={selectCls}
                            aria-label="Select contract month to view options chain"
                        >
                            {(optionsData.months || []).map(m => (
                                <option key={m} value={m}>{formatMonth(m)}</option>
                            ))}
                        </select>
                        {selectedMonth && forecastBBL != null && (
                            <span className={`text-sm ${textMuted}`}>
                                Forecast volume:
                                <span className={`ml-1 font-semibold ${textSm}`}>
                                    {formatNumber(forecastBBL)} BBL
                                </span>
                            </span>
                        )}
                        {selectedMonth && atmStrike != null && (
                            <span className={`text-sm ${textMuted}`}>
                                ATM strike:
                                <span className={`ml-1 font-semibold ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>
                                    {formatPrice(atmStrike)}
                                </span>
                            </span>
                        )}
                    </div>

                    {/* ── Options Chain Table ─────────────────────────────────── */}
                    <section
                        className={`${cardBg} rounded-xl border ${border} overflow-hidden`}
                        aria-labelledby="chain-title"
                    >
                        <div className={`px-4 py-3 border-b ${border} flex items-center justify-between`}>
                            <div>
                                <h2 id="chain-title" className={`font-semibold ${text}`}>
                                    Options Chain
                                    {selectedMonth && (
                                        <span className={`ml-2 text-sm font-normal ${textMuted}`}>
                                            {formatMonth(selectedMonth)}
                                        </span>
                                    )}
                                </h2>
                                <p className={`text-xs mt-0.5 ${textMuted}`}>
                                    CME straddle format &mdash; calls left, puts right
                                </p>
                            </div>
                            {/* ATM legend */}
                            <div className="flex items-center gap-2 text-xs">
                                <span className={`inline-block w-3 h-3 rounded ${isDark ? 'bg-yellow-600/40' : 'bg-yellow-100'} border ${isDark ? 'border-yellow-500/60' : 'border-yellow-400'}`} />
                                <span className={textMuted}>At-the-money</span>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table
                                className="w-full text-xs"
                                role="table"
                                aria-label={`Options chain for ${formatMonth(selectedMonth)}`}
                            >
                                <thead>
                                    <tr className={`${isDark ? 'bg-slate-800/80 text-gray-400' : 'bg-gray-50 text-gray-500'} border-b ${border}`}>
                                        {/* CALLS side */}
                                        <th className="px-2 py-2 text-right font-semibold">Last</th>
                                        <th className="px-2 py-2 text-right font-semibold">Prior</th>
                                        <th className={`px-2 py-2 text-right font-semibold`}>Chg</th>
                                        <th className="px-2 py-2 text-right font-semibold">Volume</th>
                                        <th className="px-2 py-2 text-right font-semibold">OI</th>
                                        {/* Center */}
                                        <th className={`px-3 py-2 text-center font-bold text-sm ${isDark ? 'text-white' : 'text-gray-900'} min-w-[80px]`}>
                                            STRIKE
                                        </th>
                                        {/* PUTS side */}
                                        <th className="px-2 py-2 text-left font-semibold">Last</th>
                                        <th className="px-2 py-2 text-left font-semibold">Prior</th>
                                        <th className="px-2 py-2 text-left font-semibold">Chg</th>
                                        <th className="px-2 py-2 text-left font-semibold">Volume</th>
                                        <th className="px-2 py-2 text-left font-semibold">OI</th>
                                    </tr>
                                    <tr className={`${isDark ? 'bg-slate-900/50 text-gray-500' : 'bg-gray-50/70 text-gray-400'} border-b ${border}`}>
                                        <th colSpan={5} className="px-2 py-1 text-center font-medium text-[10px] tracking-widest uppercase">
                                            Calls
                                        </th>
                                        <th className="px-3 py-1" />
                                        <th colSpan={5} className="px-2 py-1 text-center font-medium text-[10px] tracking-widest uppercase">
                                            Puts
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {strikes.map((strike, i) => {
                                        const call = strikeMap[strike]?.CALL
                                        const put  = strikeMap[strike]?.PUT
                                        const isATM = strike === atmStrike
                                        const rowBase = isATM
                                            ? isDark
                                                ? 'bg-yellow-900/20 border-yellow-700/40'
                                                : 'bg-yellow-50 border-yellow-200'
                                            : i % 2 === 0
                                                ? isDark ? 'bg-slate-800/40' : 'bg-white'
                                                : isDark ? 'bg-slate-800/15' : 'bg-gray-50/60'
                                        const rowHover = isDark ? 'hover:bg-slate-700/30' : 'hover:bg-blue-50/40'

                                        return (
                                            <tr
                                                key={strike}
                                                className={`${rowBase} ${rowHover} border-b ${border} transition-colors`}
                                                role="row"
                                                aria-label={`Strike ${formatPrice(strike)}${isATM ? ' (at the money)' : ''}`}
                                            >
                                                {/* CALL: Last */}
                                                <td className={`px-2 py-1.5 text-right font-mono ${textSm}`}>
                                                    {call?.LastPrice != null ? formatPrice(call.LastPrice) : '—'}
                                                </td>
                                                {/* CALL: Prior Settle */}
                                                <td className={`px-2 py-1.5 text-right font-mono ${textMuted}`}>
                                                    {call?.PriorSettle != null ? formatPrice(call.PriorSettle) : '—'}
                                                </td>
                                                {/* CALL: Daily Change */}
                                                <td className={`px-2 py-1.5 text-right font-mono font-semibold ${changeColor(call?.DailyChange)}`}>
                                                    {call?.DailyChange != null
                                                        ? `${call.DailyChange > 0 ? '+' : ''}${call.DailyChange.toFixed(2)}`
                                                        : '—'
                                                    }
                                                </td>
                                                {/* CALL: Volume */}
                                                <td className={`px-2 py-1.5 text-right ${textMuted}`}>
                                                    {call?.Volume != null ? formatNumber(call.Volume) : '—'}
                                                </td>
                                                {/* CALL: Open Interest */}
                                                <td className={`px-2 py-1.5 text-right ${textMuted}`}>
                                                    {call?.OpenInterest != null ? formatNumber(call.OpenInterest) : '—'}
                                                </td>

                                                {/* CENTER: Strike */}
                                                <td className={`px-3 py-1.5 text-center font-bold ${
                                                    isATM
                                                        ? isDark ? 'text-yellow-300' : 'text-yellow-700'
                                                        : isDark ? 'text-white' : 'text-gray-900'
                                                }`}>
                                                    {formatPrice(strike)}
                                                    {isATM && (
                                                        <span className={`block text-[9px] font-normal leading-none ${
                                                            isDark ? 'text-yellow-500' : 'text-yellow-600'
                                                        }`}>
                                                            ATM
                                                        </span>
                                                    )}
                                                </td>

                                                {/* PUT: Last */}
                                                <td className={`px-2 py-1.5 text-left font-mono ${textSm}`}>
                                                    {put?.LastPrice != null ? formatPrice(put.LastPrice) : '—'}
                                                </td>
                                                {/* PUT: Prior Settle */}
                                                <td className={`px-2 py-1.5 text-left font-mono ${textMuted}`}>
                                                    {put?.PriorSettle != null ? formatPrice(put.PriorSettle) : '—'}
                                                </td>
                                                {/* PUT: Daily Change */}
                                                <td className={`px-2 py-1.5 text-left font-mono font-semibold ${changeColor(put?.DailyChange)}`}>
                                                    {put?.DailyChange != null
                                                        ? `${put.DailyChange > 0 ? '+' : ''}${put.DailyChange.toFixed(2)}`
                                                        : '—'
                                                    }
                                                </td>
                                                {/* PUT: Volume */}
                                                <td className={`px-2 py-1.5 text-left ${textMuted}`}>
                                                    {put?.Volume != null ? formatNumber(put.Volume) : '—'}
                                                </td>
                                                {/* PUT: Open Interest */}
                                                <td className={`px-2 py-1.5 text-left ${textMuted}`}>
                                                    {put?.OpenInterest != null ? formatNumber(put.OpenInterest) : '—'}
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </section>

                    {/* ── Two-column: Calculator + IV Chart ───────────────────── */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                        {/* ── Premium Cost Calculator ───────────────────────── */}
                        <section
                            className={`${cardBg} rounded-xl border ${border} p-4`}
                            aria-labelledby="calc-title"
                        >
                            <div className="mb-4">
                                <h2 id="calc-title" className={`font-semibold ${text}`}>
                                    Premium Cost Calculator
                                </h2>
                                <p className={`text-xs mt-0.5 ${textMuted}`}>
                                    Compare puts and collars &mdash; {selectedMonth ? formatMonth(selectedMonth) : '—'}
                                    {forecastBBL != null && ` &bull; ${formatNumber(forecastBBL)} BBL forecast`}
                                </p>
                            </div>

                            {/* Column headers */}
                            <div className={`grid grid-cols-4 gap-2 text-[10px] font-semibold uppercase tracking-wide mb-2 ${textMuted}`}>
                                <span>Put Strike</span>
                                <span>Call Strike</span>
                                <span className="text-right">Net Premium/BBL</span>
                                <span className="text-right">Total Cost</span>
                            </div>

                            {/* 3 comparison rows */}
                            {[0, 1, 2].map(idx => {
                                const putVal  = calcPutStrikes[idx]
                                const callVal = calcCallStrikes[idx]
                                const putStrike  = putVal  !== '' ? Number(putVal)  : null
                                const callStrike = callVal !== '' ? Number(callVal) : null
                                const row = calcRowPremium(putStrike, callStrike)
                                const hasCalc = putStrike != null || callStrike != null

                                // Net premium/BBL display logic
                                let netPerBBL = null
                                let netLabel  = ''
                                if (putStrike != null && callStrike != null && row.netCollarPerBBL != null) {
                                    netPerBBL = row.netCollarPerBBL
                                    netLabel  = 'collar net'
                                } else if (putStrike != null && row.putPremiumPerBBL != null) {
                                    netPerBBL = row.putPremiumPerBBL
                                    netLabel  = 'put'
                                } else if (callStrike != null && row.callPremiumPerBBL != null) {
                                    netPerBBL = -row.callPremiumPerBBL   // call = credit received
                                    netLabel  = 'call credit'
                                }

                                const totalCost = netPerBBL != null && row.volumeBBL > 0
                                    ? netPerBBL * row.volumeBBL
                                    : null

                                return (
                                    <div
                                        key={idx}
                                        className={`grid grid-cols-4 gap-2 items-center py-2 border-t ${border}`}
                                    >
                                        {/* Put strike selector */}
                                        <select
                                            value={calcPutStrikes[idx]}
                                            onChange={e => handlePutStrikeChange(idx, e.target.value)}
                                            className={`${selectCls} w-full`}
                                            aria-label={`Put strike for comparison row ${idx + 1}`}
                                        >
                                            <option value="">— None —</option>
                                            {putStrikeOptions.map(s => (
                                                <option key={s} value={s}>{formatPrice(s)}</option>
                                            ))}
                                        </select>

                                        {/* Call strike selector */}
                                        <select
                                            value={calcCallStrikes[idx]}
                                            onChange={e => handleCallStrikeChange(idx, e.target.value)}
                                            className={`${selectCls} w-full`}
                                            aria-label={`Call strike for comparison row ${idx + 1}`}
                                        >
                                            <option value="">— None —</option>
                                            {callStrikeOptions.map(s => (
                                                <option key={s} value={s}>{formatPrice(s)}</option>
                                            ))}
                                        </select>

                                        {/* Net premium/BBL */}
                                        <div className="text-right">
                                            {hasCalc && netPerBBL != null ? (
                                                <>
                                                    <span className={`font-mono font-semibold ${
                                                        netPerBBL > 0
                                                            ? isDark ? 'text-red-400' : 'text-red-600'
                                                            : isDark ? 'text-green-400' : 'text-green-600'
                                                    }`}>
                                                        {netPerBBL > 0 ? '' : '+'}{Math.abs(netPerBBL).toFixed(2)}
                                                    </span>
                                                    <span className={`block text-[10px] ${textMuted}`}>{netLabel}</span>
                                                </>
                                            ) : (
                                                <span className={textMuted}>—</span>
                                            )}
                                        </div>

                                        {/* Total cost */}
                                        <div className="text-right">
                                            {hasCalc && totalCost != null ? (
                                                <>
                                                    <span className={`font-mono font-semibold ${
                                                        totalCost > 0
                                                            ? isDark ? 'text-red-400' : 'text-red-600'
                                                            : isDark ? 'text-green-400' : 'text-green-600'
                                                    }`}>
                                                        {formatDollars(totalCost)}
                                                    </span>
                                                    <span className={`block text-[10px] ${textMuted}`}>
                                                        {formatNumber(row.volumeBBL)} BBL
                                                    </span>
                                                </>
                                            ) : (
                                                <span className={textMuted}>—</span>
                                            )}
                                        </div>
                                    </div>
                                )
                            })}

                            {/* Collar explanation note */}
                            <p className={`mt-3 text-[11px] ${textMuted} border-t ${border} pt-3`}>
                                Select both a put and call strike to calculate collar net premium.
                                Positive cost = premium paid. Negative cost = net credit received.
                            </p>

                            {/* Per-strike breakdown if first row has a put selected */}
                            {calcPutStrikes[0] !== '' && (() => {
                                const s = Number(calcPutStrikes[0])
                                const put = strikeMap[s]?.PUT
                                if (!put) return null
                                return (
                                    <div className={`mt-3 rounded-lg p-3 text-xs border ${border} ${isDark ? 'bg-slate-900/60' : 'bg-gray-50'}`}>
                                        <p className={`font-semibold mb-2 ${textSm}`}>
                                            {formatPrice(s)} Put Detail
                                        </p>
                                        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                                            <span className={textMuted}>Bid / Ask</span>
                                            <span className={`font-mono ${textSm}`}>
                                                {put.Bid != null ? put.Bid.toFixed(2) : '—'} / {put.Ask != null ? put.Ask.toFixed(2) : '—'}
                                            </span>
                                            <span className={textMuted}>Day Range</span>
                                            <span className={`font-mono ${textSm}`}>
                                                {put.LowPrice != null ? put.LowPrice.toFixed(2) : '—'} – {put.HighPrice != null ? put.HighPrice.toFixed(2) : '—'}
                                            </span>
                                            <span className={textMuted}>Impl. Vol</span>
                                            <span className={`font-mono ${textSm}`}>{formatPct(put.ImpliedVol)}</span>
                                            <span className={textMuted}>Delta</span>
                                            <span className={`font-mono ${textSm}`}>
                                                {put.Delta != null ? put.Delta.toFixed(2) : '—'}
                                            </span>
                                            <span className={textMuted}>Volume</span>
                                            <span className={`font-mono ${textSm}`}>{formatNumber(put.Volume)}</span>
                                            <span className={textMuted}>Open Int.</span>
                                            <span className={`font-mono ${textSm}`}>{formatNumber(put.OpenInterest)}</span>
                                        </div>
                                    </div>
                                )
                            })()}
                        </section>

                        {/* ── Implied Volatility Chart ──────────────────────── */}
                        <section
                            className={`${cardBg} rounded-xl border ${border} p-4`}
                            aria-labelledby="iv-title"
                        >
                            <div className="mb-4">
                                <h2 id="iv-title" className={`font-semibold ${text}`}>
                                    Implied Volatility Smile
                                    {selectedMonth && (
                                        <span className={`ml-2 text-sm font-normal ${textMuted}`}>
                                            {formatMonth(selectedMonth)}
                                        </span>
                                    )}
                                </h2>
                                <p className={`text-xs mt-0.5 ${textMuted}`}>
                                    IV by strike &mdash; puts (red) and calls (blue)
                                </p>
                            </div>

                            {hasIVData ? (
                                <ResponsiveContainer width="100%" height={320}>
                                    <LineChart
                                        data={ivChartData}
                                        margin={{ top: 10, right: 20, left: 10, bottom: 20 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                                        <XAxis
                                            dataKey="strike"
                                            tickFormatter={v => `$${v}`}
                                            tick={{ fill: axisColor, fontSize: 11 }}
                                            label={{
                                                value: 'Strike ($/BBL)',
                                                position: 'insideBottom',
                                                offset: -10,
                                                fill: axisColor,
                                                fontSize: 11,
                                            }}
                                            height={44}
                                        />
                                        <YAxis
                                            tickFormatter={v => `${v.toFixed(0)}%`}
                                            tick={{ fill: axisColor, fontSize: 11 }}
                                            domain={['auto', 'auto']}
                                            width={48}
                                            label={{
                                                value: 'IV (%)',
                                                angle: -90,
                                                position: 'insideLeft',
                                                offset: 10,
                                                fill: axisColor,
                                                fontSize: 11,
                                            }}
                                        />
                                        <Tooltip content={<IVTooltip isDark={isDark} />} />
                                        <Legend
                                            wrapperStyle={{ paddingTop: 8, fontSize: 12, color: axisColor }}
                                        />
                                        {/* ATM reference line */}
                                        {atmStrike != null && (
                                            <line
                                                x1={0} x2={0}
                                                stroke={isDark ? '#fbbf24' : '#d97706'}
                                                strokeDasharray="4 3"
                                            />
                                        )}
                                        <Line
                                            type="monotone"
                                            dataKey="putIV"
                                            name="Put IV"
                                            stroke="#ef4444"
                                            strokeWidth={2}
                                            dot={{ r: 3, fill: '#ef4444', strokeWidth: 0 }}
                                            activeDot={{ r: 5 }}
                                            connectNulls={false}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="callIV"
                                            name="Call IV"
                                            stroke="#3b82f6"
                                            strokeWidth={2}
                                            dot={{ r: 3, fill: '#3b82f6', strokeWidth: 0 }}
                                            activeDot={{ r: 5 }}
                                            connectNulls={false}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className={`flex flex-col items-center justify-center h-64 gap-2 ${textMuted}`}>
                                    <svg className="w-10 h-10 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                    <p className="text-sm">Implied volatility data not available for this month.</p>
                                </div>
                            )}

                            {/* IV summary stats */}
                            {hasIVData && (() => {
                                const putIVs  = ivChartData.filter(d => d.putIV  != null).map(d => d.putIV)
                                const callIVs = ivChartData.filter(d => d.callIV != null).map(d => d.callIV)
                                const atmPutIV  = atmStrike != null && strikeMap[atmStrike]?.PUT?.ImpliedVol != null
                                    ? strikeMap[atmStrike].PUT.ImpliedVol * 100
                                    : null
                                const atmCallIV = atmStrike != null && strikeMap[atmStrike]?.CALL?.ImpliedVol != null
                                    ? strikeMap[atmStrike].CALL.ImpliedVol * 100
                                    : null
                                const avgPutIV  = putIVs.length  > 0 ? putIVs.reduce((a, b) => a + b, 0)  / putIVs.length  : null
                                const avgCallIV = callIVs.length > 0 ? callIVs.reduce((a, b) => a + b, 0) / callIVs.length : null
                                return (
                                    <div className={`mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs border-t ${border} pt-3`}>
                                        <div className="text-center">
                                            <p className={textMuted}>ATM Put IV</p>
                                            <p className={`font-semibold ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                                                {atmPutIV != null ? `${atmPutIV.toFixed(1)}%` : '—'}
                                            </p>
                                        </div>
                                        <div className="text-center">
                                            <p className={textMuted}>ATM Call IV</p>
                                            <p className={`font-semibold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                                                {atmCallIV != null ? `${atmCallIV.toFixed(1)}%` : '—'}
                                            </p>
                                        </div>
                                        <div className="text-center">
                                            <p className={textMuted}>Avg Put IV</p>
                                            <p className={`font-semibold ${textSm}`}>
                                                {avgPutIV != null ? `${avgPutIV.toFixed(1)}%` : '—'}
                                            </p>
                                        </div>
                                        <div className="text-center">
                                            <p className={textMuted}>Avg Call IV</p>
                                            <p className={`font-semibold ${textSm}`}>
                                                {avgCallIV != null ? `${avgCallIV.toFixed(1)}%` : '—'}
                                            </p>
                                        </div>
                                    </div>
                                )
                            })()}
                        </section>
                    </div>

                </div>
            )}
        </div>
    )
}
